import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { useSnackbar } from 'notistack';
import { SettingDisplay } from 'lib/SettingDisplay';
import { isString } from 'lib/typeguards';
import { copyToClipboard } from 'lib/helpers';

interface SettingsDisplayCopyToClipboardProps {
    title?: string;
    text?: React.ReactNode;
    value: string;
    description?: string;
    crop?: boolean;
    buildtimeIOS?: boolean;
    buildtimeAndroid?: boolean;
    snackbarText?: string;
    variant?: 'body2' | 'body1';
}

export const SettingsDisplayCopyToClipboard: React.FC<SettingsDisplayCopyToClipboardProps> = ({
    text,
    title,
    description,
    value,
    crop,
    buildtimeIOS,
    buildtimeAndroid,
    variant = 'body2',
    snackbarText = 'Setting value copied to clipboard.'
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const displayValue = React.useMemo(() => {
        if (crop && isString(text) && text.length > 50) {
            return `${text.substring(0, 50)}...`;
        }
        return text;
    }, [crop, text]);

    const handleClipboardClick = React.useCallback(() => {
        copyToClipboard(value);
        enqueueSnackbar(snackbarText, {
            variant: 'default'
        });
    }, [enqueueSnackbar, value, snackbarText]);

    const hasValue = React.useMemo(() => value && value.length > 0, [value]);

    return (
        <SettingDisplay
            label={title}
            description={description}
            buildtimeIOS={buildtimeIOS}
            buildtimeAndroid={buildtimeAndroid}
        >
            <Box display="flex" alignItems="start" width="100%" gap={1}>
                <Typography variant={variant} sx={{ wordBreak: 'break-word' }}>
                    {displayValue}
                </Typography>
                {hasValue && (
                    <IconButton size="small" onClick={handleClipboardClick}>
                        <ContentCopyIcon />
                    </IconButton>
                )}
            </Box>
        </SettingDisplay>
    );
};
